import { useChartStore } from '@/store/chartStore';
import { Modal } from '@/components/ui/Modal';
import { Z_INDEX } from '@/constants/zIndex';
import { DEFAULT_CUP_HANDLE_SETTINGS } from '@/indicators/cupHandle';
import { NumberField } from '@/components/Chart/ConfigModalFields';
import type { CupHandleSettings } from '@/types';

interface Props {
  isActive: boolean;
  onClose: () => void;
  onAdd?: () => void;
}

function triggerRedraw() {
  const chart = useChartStore.getState().chart;
  const paneId = useChartStore.getState().indicatorPaneMap.get('CUP_HANDLE');
  if (chart && paneId) {
    try {
      (chart as unknown as { overrideIndicator: (o: { name: string; styles?: Record<string, unknown> }, paneId?: string) => void })
        .overrideIndicator({ name: 'CUP_HANDLE', styles: {} }, paneId);
    } catch { /* ignore */ }
  }
}

export function CupHandleConfigModal({ isActive, onClose, onAdd }: Props) {
  const cupHandleSettings = useChartStore((s) => s.cupHandleSettings);
  const setCupHandleSettings = useChartStore((s) => s.setCupHandleSettings);
  const setCupHandleEnabled = useChartStore((s) => s.setCupHandleEnabled);

  function patch<K extends keyof CupHandleSettings>(key: K, value: CupHandleSettings[K]) {
    setCupHandleSettings({ [key]: value } as Partial<CupHandleSettings>);
    triggerRedraw();
  }

  function handleReset() {
    setCupHandleSettings(DEFAULT_CUP_HANDLE_SETTINGS);
    triggerRedraw();
  }

  function handleRemove() {
    setCupHandleEnabled(false);
    const chart = useChartStore.getState().chart;
    const paneMap = useChartStore.getState().indicatorPaneMap;
    if (chart && paneMap.has('CUP_HANDLE')) {
      try {
        (chart as unknown as { removeIndicator: (paneId: string, name?: string) => void })
          .removeIndicator('candle_pane', 'CUP_HANDLE');
      } catch { /* ignore */ }
      const next = new Map(paneMap);
      next.delete('CUP_HANDLE');
      useChartStore.getState().setIndicatorPaneMap(next);
    }
    onClose();
  }

  return (
    <Modal onClose={onClose} size="xl" zIndex={Z_INDEX.modalElevated} className="max-h-[min(90vh,680px)]">
      <Modal.Header
        onClose={onClose}
        title={
          <div className="flex items-center gap-2.5">
            <span className="text-base font-bold text-modal-text">Cup & Handle</span>
            <span className="text-sm text-modal-muted">— Pattern overlay with confidence score</span>
            {isActive && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-accent/15 text-accent font-medium">
                Active
              </span>
            )}
          </div>
        }
      />

      {/* Body */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Left: Settings */}
        <div className="w-[55%] border-r border-border flex flex-col overflow-y-auto">
          <div className="px-5 py-4 flex flex-col gap-4">

            <p className="text-xs font-semibold text-modal-muted uppercase tracking-wider">Base &amp; Cup</p>
            <NumberField
              label="Pivot bars" hint="Bars each side to confirm a rim as a local high"
              value={cupHandleSettings.pivotBars} onChange={(v) => patch('pivotBars', v)}
              min={2} max={50}
            />
            <NumberField
              label="Rim window" hint="Rolling window used to scan for the base rim"
              value={cupHandleSettings.rimWindowBars} onChange={(v) => patch('rimWindowBars', v)}
              min={5} max={100}
            />
            <NumberField
              label="Prior leg-up bars" hint="Lookback for the advance feeding the base"
              value={cupHandleSettings.priorLegBars} onChange={(v) => patch('priorLegBars', v)}
              min={10} max={252}
            />
            <NumberField
              label="Min leg-up" hint="Minimum prior advance into the rim"
              value={Math.round(cupHandleSettings.minLegUpPct * 100)} onChange={(v) => patch('minLegUpPct', v / 100)}
              min={5} max={100} suffix="%"
            />
            <div className="h-px bg-border" />
            <NumberField
              label="Min length" hint="Minimum cup duration, in bars"
              value={cupHandleSettings.minLengthBars} onChange={(v) => patch('minLengthBars', v)}
              min={5} max={cupHandleSettings.maxLengthBars}
            />
            <NumberField
              label="Max length" hint="Maximum cup duration, in bars"
              value={cupHandleSettings.maxLengthBars} onChange={(v) => patch('maxLengthBars', v)}
              min={cupHandleSettings.minLengthBars} max={500}
            />
            <NumberField
              label="Max depth" hint="Maximum cup depth below the rim"
              value={Math.round(cupHandleSettings.maxDepthPct * 100)} onChange={(v) => patch('maxDepthPct', v / 100)}
              min={9} max={80} suffix="%"
            />

            <div className="h-px bg-border" />

            <p className="text-xs font-semibold text-modal-muted uppercase tracking-wider">Handle</p>
            <NumberField
              label="Trigger distance" hint="How close to the rim before tracking a handle"
              value={Math.round(cupHandleSettings.handleTriggerPct * 100)} onChange={(v) => patch('handleTriggerPct', v / 100)}
              min={1} max={30} suffix="%"
            />
            <NumberField
              label="Max handle depth" hint="Maximum handle pullback, relative to cup depth"
              value={Math.round(cupHandleSettings.maxHandleDepthPct * 100)} onChange={(v) => patch('maxHandleDepthPct', v / 100)}
              min={5} max={100} suffix="%"
            />
            <NumberField
              label="Min handle length" hint="Minimum handle duration, in bars"
              value={cupHandleSettings.minHandleBars} onChange={(v) => patch('minHandleBars', v)}
              min={1} max={cupHandleSettings.maxHandleBars}
            />
            <NumberField
              label="Max handle length" hint="Maximum handle duration, in bars"
              value={cupHandleSettings.maxHandleBars} onChange={(v) => patch('maxHandleBars', v)}
              min={cupHandleSettings.minHandleBars} max={130}
            />

            <div className="h-px bg-border" />

            <p className="text-xs font-semibold text-modal-muted uppercase tracking-wider">Display</p>
            <NumberField
              label="Min confidence to show" hint="Hide the pattern below this score"
              value={cupHandleSettings.minConfidenceToShow} onChange={(v) => patch('minConfidenceToShow', v)}
              min={0} max={100} suffix="%"
            />

            <button
              type="button"
              onClick={handleReset}
              className="self-start text-xs text-modal-muted hover:text-modal-text underline transition-colors"
            >
              Reset to defaults
            </button>
          </div>
        </div>

        {/* Right: About */}
        <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-4">
          <p className="text-xs font-semibold text-modal-muted uppercase tracking-wider">About</p>
          <p className="text-sm text-modal-text leading-relaxed">
            Detects the most recent IBD/MarketSmith-style cup-and-handle base: a rounded
            consolidation (the "cup") fed by a genuine prior uptrend, followed by a shallow
            pullback confined to the upper half of the cup (the "handle") before a breakout.
          </p>
          <p className="text-sm text-modal-text leading-relaxed">
            Only the single most recent qualifying pattern is drawn. A confidence score (0-100%)
            blends how well the depth, length, U-shape roundness, rim symmetry, handle quality,
            and prior-trend strength fit the ideal pattern shape.
          </p>

          <div className="h-px bg-border" />

          <p className="text-xs font-semibold text-modal-muted uppercase tracking-wider">Legend</p>
          <div className="flex flex-col gap-1.5 text-xs text-modal-muted">
            <div className="flex items-center gap-2">
              <span className="w-3 h-0.5 rounded-full" style={{ background: 'rgba(96,165,250,0.9)' }} />
              <span>Cup floor (rounded curve through the low)</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-0.5 border-t border-dashed" style={{ borderColor: 'rgba(146,193,131,0.9)' }} />
              <span>Base rim (left high to right high)</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-sm" style={{ background: 'rgba(251,191,36,0.15)', border: '1px dashed rgba(251,191,36,0.9)' }} />
              <span>Handle (pullback in the upper half of the cup)</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-0.5 border-t border-dashed" style={{ borderColor: 'rgba(168,85,247,0.85)' }} />
              <span>Buy point (breakout line)</span>
            </div>
          </div>
        </div>
      </div>

      <Modal.Footer className="!justify-between">
        {isActive ? (
          <button
            type="button"
            onClick={handleRemove}
            className="text-sm px-3 py-2 rounded-lg border border-red-500/40 text-red-400 hover:bg-red-500/10 transition-colors"
          >
            Remove Indicator
          </button>
        ) : (
          <span />
        )}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            className="text-sm px-3 py-2 rounded-lg text-modal-muted hover:text-modal-text hover:bg-modal-hover transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={isActive || !onAdd ? onClose : () => { onAdd(); onClose(); }}
            className="text-sm px-4 py-2 rounded-lg bg-accent text-white font-medium hover:bg-accent/90 transition-colors"
          >
            {isActive ? 'Done' : 'Add to Chart'}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
